import React from 'react';

import { ChapterWrapper } from '~/ChapterWrapper';
import { ELink } from '~/ExternalLink';
import Footer from '~/Footer';
import { Path } from '~/paths';

const ex_a_boxplot_1 = require('../assets/images/ex_a_boxplot_1.png');
const ex_a_boxplot_2 = require('../assets/images/ex_a_boxplot_2.png');
const ex_b_barchart = require('../assets/images/ex_b_barchart.png');
const ex_b_histogram = require('../assets/images/ex_b_histogram.png');
const ex_b_histmedian = require('../assets/images/ex_b_histmedian.png');

export const ExDescriptiveStatsMeta = {
    title: "Cvičení: Popisná statistika",
    shortTitle: "Popisná st.",
    path: Path.ex_descriptive_stats,
    element: (sectionNumber:string) => <ExDescriptiveStats sectionNumber={sectionNumber}/>,
    sectionNumber: "",
}

const TOCSpec = [
    "ex-a-quantiles",
    "ex-a-histograms",
    "ex-a-meansd",
];

export function ExDescriptiveStats({sectionNumber}: {sectionNumber: string}) {

    return (
        <ChapterWrapper sectionNumber={sectionNumber} title={ExDescriptiveStatsMeta.title} TOCSpec={TOCSpec}>

        <h2 id="ex-a-quantiles">A. Kvantily, boxplot</h2>

        <p>1. Určete rozsah, variační obor a variační rozpětí následujících datových souborů:
        <ol className="alpha-list">
            <li>{"$\\, 0.2, \\, 0.4, \\, 0.5$"}</li>
            <li>{"$\\, 10, \\, 11, \\, 10, \\, 12, \\, 10, \\, 10, \\, 10, \\, 8, \\, 11$"}</li>
        </ol>
        </p>

        <p>2. Určete medián následujících datových souborů:
        <ol className="alpha-list">
            <li>{"$\\, 9.8, \\, 10.0, \\, 10.0, \\, 10.0, \\, 10.0$"}</li>
            <li>{"$\\, 5, \\,  6, \\,  7, \\,  10$"}</li>
        </ol>
        </p>

        <p>3. Určete horní a dolní kvartily následujících datových souborů:
        <ol className="alpha-list">
            <li>{"$\\,  1, \\, 2, \\, 3, \\, 4$"}</li>
            <li>{"$\\,  4, \\, 5, \\, 5, \\, 5, \\, 5, \\, 5$"}</li>
        </ol>
        </p>

        <p>4. Určete kvantily {"$q_{0.45}$"}, {"$q_{0.5}$"} a {"$q_{0.55}$"} datového
            souboru {"$\\, 1, \\, 2, \\, 3, \\, 4, \\, 5$"}.
        </p>

        <p>5. Jakému rozsahu kvantilů odpovídá {"$x = 3$"} v následujících datových souborech?
        <ol className="alpha-list">
            <li>{"$\\, 1, \\, 2, \\, 3, \\, 4, \\, 5$"}</li>
            <li>{"$\\, 1, \\, 2, \\, 3, \\, 3, \\, 3, \\, 5$"}</li>
        </ol>
        </p>

        <p>6. Zkonstruujte krabicový graf (boxplot) pro následující datové soubory:
        <ol className="alpha-list">
            <li>{"$\\, 0.85, \\, 0.82, \\, 0.83, \\, 0.86, \\, 0.85, \\, 0.85, \\, 0.87, \\, 0.86, \\, 0.75, \\, 0.83$"} </li>
            <li>{"$\\, 2, \\, 2, \\, 3, \\, 3, \\, 3, \\, 4, \\, 4, \\, 4, \\, 4, \\, 5, \\, 5, \\, 6, \\, 7, \\, 13, \\, 16$"} </li>
        </ol>
        </p>

        <p>7. O kolik nejvíc dokážete ovlivnit medián (jaké nejnižší a nejvyšší hodnoty mediánu zvládnete dosáhnout),
        máte-li povoleno pohnout (libovolně) s jedním datovým bodem (dle vlastního výběru) v následujících datových souborech?
        {/* (Libovolně pohnout znamená nahradit hodnotu jednoho datového bodu jinou hodnotou - např. v případě a) mohu hodnotu 20 nahradit hodnotou -100.) */}
        <ol className="alpha-list">
            <li>{"$\\, 10, \\, 14, \\, 15, \\, 16, \\, 20$"} </li>
            <li>{"$\\, 10, \\, 13, \\, 14, \\, 16, \\, 17, \\, 20$"} </li>
        </ol>
        </p>

        <p style={{color: "grey"}}>Výsledky:
        <ol className="main-ex-list">
            <li>
                <ol className="alpha-list">
                <li>Rozsah: {"$n = 3$"}, variační obor: {"$\\langle 0.2, 0.5\\rangle$"}, variační rozpětí: {"$R = 0.5 - 0.2 = 0.3$"}</li>
                <li>Rozsah: {"$n = 9$"}, variační obor: {"$\\langle 8, 12\\rangle$"}, variační rozpětí: {"$R = 12 - 8 = 4$"}</li>
                </ol>
            </li>
            <li>
                <ol className="alpha-list">
                    <li>{"$n=5$"}, {"$n \\cdot 0.5 = 2.5$"}, {"$\\lceil 2.5 \\rceil= 3$"}, medián: {"$\\tilde{x} = x_{(3)} = 10.0$"} </li>
                    <li>{"$n=4$"}, {"$n \\cdot 0.5 = 2$"}, medián: {"$\\tilde{x} = \\frac{1}{2}(x_{(2)} + x_{(3)}) = \\frac{1}{2}(6 + 7) = 6.5$"} </li>
                </ol>
            </li>
            <li>
                <ol className="alpha-list">
                <li>dolní kvartil: {"$q_{0.25} = 1.5$"} horní kvartil: {"$q_{0.75} = 3.5$"}</li>
                <li>dolní kvartil: {"$q_{0.25} = 5$"} horní kvartil: {"$q_{0.75} = 5$"}</li>
                </ol>
            </li>
            <li>{"$q_{0.45} = 3$"}, {"$q_{0.5} = 3$"}, {"$q_{0.55} = 3$"}</li>
            <li>
            <ol className="alpha-list">
                <li>
                Máme {"$n=5$"}; pro {"$p=0.4$"} vychází ještě {"$np = 2$"}, což vede na kvantil {"$q_{0.4} = 2.5$"}.
                Jakmile je {"$0.4 < p < 0.6$"}, je {"$2 < np < 3, \\lceil np \\rceil = 3$"}, což vede na kvantil {"$q_{p} = 3$"}.
                Pro {"$p=0.6$"} už vychází {"$np = 3$"}, což vede na kvantil {"$q_{0.6} = 3.5$"}.
                Vidíme tedy, že hodnota {"$x=3$"} odpovídá rozsahu kvantilů {"$q_{0.4}$"} až {"$q_{0.6}$"} bez krajních hodnot,
                jinak napsáno {"$q_{p} = 3$"} pro {"$p \\in ( 0.4, 0.6 )$"}.
            </li>
            <li>
            Kvantil {"$q_{p} = 3$"} pro {"$p \\in \\left( \\frac{2}{6}, \\frac{5}{6} \\right)$"}.
            </li>
            </ol>
            </li>

            <li>
            <ol className="alpha-list">
                <li>
                    <div style={{textAlign: "center", paddingBottom: 10}}>
                        <img src={ex_a_boxplot_1} style={{width: 350}}/>
                    </div>
                </li>
                <li>
                    <div style={{textAlign: "center", paddingBottom: 10}}>
                        <img src={ex_a_boxplot_2} style={{width: 350}}/>
                    </div>
                </li>
            </ol>
            </li>
            <li>
                <ol className="alpha-list">
                    <li>Nejméně {"$14$"}, nejvýše {"$16$"}.</li>
                    <li>Nejméně {"$13.5$"}, nejvýše {"$16.5$"}.</li>
                </ol>
            </li>
        </ol>
        </p>

        <p><i>Pozn.: Pokud Vám např. v R vyjde boxplot jinak, je to proto, že R počítá jinak kvantily.
        My jsme viděli dva způsoby (s průměrováním a bez); R umí 9 způsobů výpočtu (!),
        <ELink href={"https://www.rdocumentation.org/packages/stats/versions/3.6.2/topics/quantile"}> viz Types</ELink>.
        Každý ze způsobů je nějak motivován (např. jako odhad kvantilu populace ze vzorku), a zhruba
        řečeno dávají podobné výsledky - používají nějakou interpolaci, a liší se plus minus o jednu pozici v datech.
        Na stat. souboru s velkým rozsahem nebude rozdíl nijak velký. Defaultní způsob výpočtu kvantilů pro boxplot
        v R je pravděpodobně 'typ 7'. MS Excel a jiné statistické nástroje budou v podobné pozici (který způsob použít?);
        je možné, že každý volí jiný způsob.
        </i></p>

        <h2 id="ex-a-histograms">B. Statistické znaky, třídění, sloupcový graf, histogram</h2>

        <p>1. Děláme statistický průzkum - počítáme počet vajec v ptačích hnízdech určitého druhu ptáka
        na jaře v určité přírodní rezervaci. O jaký statitický znak se jedná?
        </p>

        <p>2. <i>(Pokračování příkladu 1)</i>: Naměřili jsme následující počty vajec:<br/>
        {"$3, \\, 2, \\, 2, \\, 2, \\, 1, \\, 2, \\, 1, \\, 2, \\, 4, \\, 3, \\, $"}
        {"$\\, 2, \\, 2, \\, 5, \\, 3, \\, 4, \\, 1, \\, 1, \\, 3, \\, 4, \\, 3, \\, $"}
        {"$\\, 1, \\, 2, \\, 2, \\, 3, \\, 3, \\, 5, \\, 1, \\, 2, \\, 2, \\, 3$"}.<br/>
        Určete absolutní a relativní četnosti jednotlivých hodnot, zkonstruujte sloupcový graf;
        určete medián, modus a průměr.
        </p>

        <p>3. Při senzorické analýze degustátoři hodnotí pivo na stupnici "velmi hořké", "hořké", "středně hořké",
        "lehce hořké", "nehořké". O jaký typ statistického znaku se jedná?
        </p>

        <p>4. Na testovou otázku odpovědělo 10 studentů: 3x A, 5x B, 0x C, 2x D. Jaká je relativní
        četnost odpovědí A, B, C, D?
        </p>

        <p>5. Naměřené hustoty vzorků sledované látky byly<br/>
        {"$1.351, \\, 1.348, \\, 1.353, \\, 1.355, \\, 1.351, \\, 1.352, \\, 1.343, \\, 1.350, \\, 1.359, \\, 1.350 \\, $"}
        {"$1.347, \\, 1.350, \\, 1.351, \\, 1.346, \\, 1.356, \\, 1.351, \\, 1.355, \\, 1.349, \\, 1.350, \\, 1.353 \\, $"} <br/>
        Roztřiďte soubor na třídy s šířkou {"$0.004$"}, první třída začíná na {"$1.340$"}. Zkonstruujte histogram (hustoty).
        </p>

        <p>6. Na kolik tříd byste roztřídili soubor o rozsahu {"$n = 60$"} hodnot?
        </p>

        <p>7. Z následujícího histogramu odhadněte polohu mediánu:
            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={ex_b_histmedian} style={{width: 450}}/>
            </div>
        </p>

        <p>8. Kolegovi po roztřídění souboru vyšly relativní četnosti tříd jako {"$0.10, \\, 0.65, \\, 0.15, \\, 0.20$"}.
        Co byste mu na to řekli?
        </p>

        <p>9. Kolegyně po roztřídění spojitého znaku získala následující tabulku:
            <table className='data-table histogram-table histogram-table-ex'>
                <thead>
                    <tr>
                        <th>Třída {"$\\langle a_j; b_j)$"}</th>
                        <th>Hustota {"$\\frac{f_j}{b_j-a_j}$"}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{"$\\langle 10, 12)$"}</td>
                        <td>{"$0.1$"}</td>
                    </tr>
                    <tr>
                        <td>{"$\\langle 12, 14)$"}</td>
                        <td>{"$0.3$"}</td>
                    </tr>
                    <tr>
                        <td>{"$\\langle 14, 16)$"}</td>
                        <td>{"$?$"}</td>
                    </tr>
                </tbody>
            </table>
            Pomozte jí doplnit chybějící hodnotu.
        </p>

        <p style={{color: "grey"}}>Výsledky:
            <ol className="main-ex-list">
            <li>Je to znak kvantitativní, poměrový a diskrétní.</li>
            <li>
            <table className='data-table histogram-table histogram-table-ex'>
                <thead>
                    <tr>
                        <th>Index {"$j$"}</th>
                        <th>Hodnota {"$x_j$"}</th>
                        <th>Abs. četnost {"$c_j$"}</th>
                        <th>Rel. četnost {"$f_j = \\frac{c_j}{n}$"}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{"$1$"}</td>
                        <td>{"$1$"}</td>
                        <td>{"$6$"}</td>
                        <td>{"$\\frac{6}{30}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$2$"}</td>
                        <td>{"$2$"}</td>
                        <td>{"$11$"}</td>
                        <td>{"$\\frac{11}{30}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$3$"}</td>
                        <td>{"$3$"}</td>
                        <td>{"$8$"}</td>
                        <td>{"$\\frac{8}{30}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$4$"}</td>
                        <td>{"$4$"}</td>
                        <td>{"$3$"}</td>
                        <td>{"$\\frac{3}{30}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$5$"}</td>
                        <td>{"$5$"}</td>
                        <td>{"$2$"}</td>
                        <td>{"$\\frac{2}{30}$"}</td>
                    </tr>
                </tbody>
            </table>
            <p>
            Modus: {`$\\hat{x} = 2$`} (hodnota s největší četností).<br/>
            Průměr: {`$\\bar{x} = \\frac{6}{30}\\cdot 1 + \\frac{11}{30}\\cdot 2 + \\frac{8}{30}\\cdot 3 + \\frac{3}{30}\\cdot 4 + \\frac{2}{30}\\cdot 5 = 2.4\\bar{6}$`}.<br/>
            Medián: {`$\\tilde{x} = 2$`}. Výpočet: {"$n=30, n \\cdot 0.5 = 15, \\tilde{x} = \\frac{1}{2}(x_{(15)} + x_{(16)}) = \\frac{1}{2}(2 + 2) = 2$"}.
            </p>
                <div style={{textAlign: "center", paddingBottom: 10}}>
                    <img src={ex_b_barchart} style={{width: 450}}/>
                </div>
            </li>
            <li>Je to znak kvalitativní (tím pádem také diskrétní), ordinální (úrovně lze uspořádat).</li>
            <li>{"$\\frac{3}{10}, \\frac{1}{2}, 0, \\frac{1}{5}$"}.</li>

            <li>
            <table className='data-table histogram-table histogram-table-ex'>
                <thead>
                    <tr>
                        <th>Index třídy {"$j$"}</th>
                        <th>Třída {"$\\langle a_j; b_j)$"}</th>
                        <th>Absolutní četnost <span>{"$c_j$"}</span></th>
                        <th>Relativní četnost <span>{"$f_j = \\frac{c_j}{n}$"}</span></th>
                        <th>Hustota<span>{"$\\frac{f_j}{b_j-a_j}$"}</span></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{"$1$"}</td>
                        <td>{"$\\langle 1.340; 1.344)$"}</td>
                        <td>{"$1$"}</td>
                        <td>{"$\\frac{1}{20}$"}</td>
                        <td>{"$\\frac{1}{20 \\cdot 0.004} = 12.5$"}</td>
                    </tr>
                    <tr>
                        <td>{"$2$"}</td>
                        <td>{"$\\langle 1.344; 1.348)$"}</td>
                        <td>{"$2$"}</td>
                        <td>{"$\\frac{2}{20}$"}</td>
                        <td>{"$\\frac{2}{20 \\cdot 0.004} = 25.0$"}</td>
                    </tr>
                    <tr>
                        <td>{"$3$"}</td>
                        <td>{"$\\langle 1.348; 1.352)$"}</td>
                        <td>{"$10$"}</td>
                        <td>{"$\\frac{10}{20}$"}</td>
                        <td>{"$\\frac{10}{20 \\cdot 0.004} = 125.0$"}</td>
                    </tr>
                    <tr>
                        <td>{"$4$"}</td>
                        <td>{"$\\langle 1.352; 1.356)$"}</td>
                        <td>{"$5$"}</td>
                        <td>{"$\\frac{5}{20}$"}</td>
                        <td>{"$\\frac{5}{20 \\cdot 0.004} = 62.5$"}</td>
                    </tr>
                    <tr>
                        <td>{"$5$"}</td>
                        <td>{"$\\langle 1.356; 1.360)$"}</td>
                        <td>{"$2$"}</td>
                        <td>{"$\\frac{2}{20}$"}</td>
                        <td>{"$\\frac{2}{20 \\cdot 0.004} = 25.0$"}</td>
                    </tr>
                </tbody>
            </table>
            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={ex_b_histogram} style={{width: 450}}/>
            </div>
            </li>

            <li>Použijeme např. Sturgesovo pravidlo: {"$1 + \\log_2(60) = 6.9 \\approx 7$"} tříd.
            Odmocninové pravidlo říká, že bychom mohli volit počet tříd
            v rozmezí {"$\\sqrt{60} = 7.7 \\approx 8$"} až {"$2\\sqrt{60} = 15.5 \\approx 16$"}.
            Máme tedy představu, že {"$5$"} tříd by asi bylo málo, a {"$20$"} tříd by bylo moc.
            Konkrétní volba pak bude záviset na tom, jaký budeme chtít krok (šířku třídy) a jestli
            jsou data rozdělena spíše rovnoměrně, nebo ne.
            </li>

            <li>{"$\\tilde{x} = 5.6$"}. Viz cvičení.
            Rovnice: {"$0.25 \\cdot 0.5 + 0.25 \\cdot 1 + (\\tilde{x} - 5.5) \\cdot 1.25 = 0.5$"}.
            </li>

            <li>Součet jeho čísel je {"$1.1$"}, což není rovno jedné. Relativní četnosti tříd musí
                dávat dohromady jedničku. Upozornili bychom ho, že někde nastala numerická chyba.
            </li>

            <li>Chybějící hodnota hustoty je {"$0.1$"}. Plocha histogramu musí být jedna, délka třídy je 2:
            {"$0.1 \\cdot 2 + 0.3 \\cdot 2 + x \\cdot 2 = 1 \\ \\ \\rightarrow \\ \\ x = 0.1$"}.
            </li>
            </ol>
        </p>


        <h2 id="ex-a-meansd">C. Průměr, rozptyl, směrodatná odchylka, nerovnosti</h2>

        <p>1. Určete průměr, rozptyl a směrodatnou odchylku následujících datových souborů:
        <ol className="alpha-list">
            <li>{"$\\, 1, \\, 2, \\, 3, \\, 4, \\, 5$"} </li>
            <li>{"$\\, 1, \\, 1, \\, 3, \\, 5, \\, 5$"} </li>
        </ol>
        </p>

        <p>2. Máme datový soubor {"$x_1, x_2, \\ldots, x_n$"} hmotností v uncích (oz). Jeho průměr je {"$\\bar{x} = 1.2\\,\\text{oz}$"}.
        Jaký bude průměr {"$\\bar{y}$"} datového souboru {"$y_1, y_2, \\ldots, y_n$"} stejných hmotností v gramech (g),
        když platí, že {"$1 \\,\\text{oz} = 28.3495 \\,\\text{g}$"}?
        </p>

        <p>3. Kolegovi vyšel rozptyl {"$s_n^2 = -0.342$"}. Co byste mu na to řekli?</p>

        <p>4. Průměr 100 nezáporných hodnot vyšel {"$\\bar{x} = 12$"}. Kolik nejvýše hodnot může být větších nebo rovných {"$40$"}?
        </p>

        <p>5. Roztříděním datového souboru pro spojitý znak jsme získali následující tabulku:
            <table className='data-table histogram-table histogram-table-ex'>
                <thead>
                    <tr>
                        <th>Třída {"$\\langle a_j; b_j)$"}</th>
                        <th>Abs. četnost {"$c_j$"}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{"$\\langle 0, 10)$"}</td>
                        <td>{"$12$"}</td>
                    </tr>
                    <tr>
                        <td>{"$\\langle 10, 20)$"}</td>
                        <td>{"$8$"}</td>
                    </tr>
                    <tr>
                        <td>{"$\\langle 20, 30)$"}</td>
                        <td>{"$4$"}</td>
                    </tr>
                    <tr>
                        <td>{"$\\langle 30, 40)$"}</td>
                        <td>{"$2$"}</td>
                    </tr>
                </tbody>
            </table>
            Původní datový soubor se ztratil. Určete průměr čistě na základě této tabulky. Může vyjít jinak,
            než u původního souboru?
        </p>

        <p>6. Máme datový soubor {"$x_1, x_2, \\ldots, x_n$"} hmotností v uncích (oz). Jeho směrodatná odchylka je {"$s_n(x) = 3.1\\,\\text{oz}$"}.
        Jaká bude směrodatná odchylka {"$s_n(y)$"} datového souboru {"$y_1, y_2, \\ldots, y_n$"} stejných hmotností v gramech (g),
        když platí, že {"$1 \\,\\text{oz} = 28.3495 \\,\\text{g}$"}?
        </p>

        <p>7. Máme datový soubor {"$x_1, x_2, \\ldots, x_n$"}, jehož směrodatná odchylka je {"$s_n(x) = 2.5$"}.
        Vytvoříme datový soubor {"$y_1, y_2, \\ldots, y_n$"} tak, že {"$y_i = x_i + 10$"}. Jaká bude směrodatná odchylka {"$s_n(y)$"}?
        </p>

        <p>8. V jakém případě může vyjít rozptyl nulový?</p>

        <p style={{color: "grey"}}>Výsledky:<br/>
        <ol className="main-ex-list">
            <li>
                a) {"$\\bar{x} = 3, s_n^2 = 2, s_n = \\sqrt{2} \\approx 1.41$"}
                {"$\\quad$"} b) {"$\\bar{x} = 3, s_n^2 = 3.2, s_n \\sqrt{3.2} \\approx 1.78$"}<br/>
                <i>Optickým srovnáním obou souborů by mělo by intuitivně zřejmé, že druhý soubor musí mít
                větší rozptyl - dvě z jeho hodnot mají větší vzdálenost od průměru než v prvním souboru.
                </i>
            </li>
            <li>{"$\\bar{y} = 1.2 \\cdot 28.3495 \\approx 34.02$"} g.</li>
            <li>Že má někde numerickou chybu - rozptyl nemůže nikdy vyjít záporný.</li>
            <li>Markovova nerovnost. Hodnota {"$40$"} je jakým násobkem průměru {"$12$"}? {"$40/12 = 3.\\bar{3}$"}.
            Markovova nerovnost říká, že nejvýše {"$1/3.\\bar{3} = \\frac{12}{40} = 0.3$"} ({"$=30\\%$"})
            hodnot může být větších nebo rovných {"$40$"}. Pro soubor {"$100$"} hodnot to znamená nejvýše {"$30$"} hodnot.<br/>
            <i>Pozn.: Jak by to v tomto (krajním) případě vypadalo? Měli bychom soubor {"$70 \\times 0$"} a {"$30 \\times 40$"} hodnot, a
                průměr by byl {"$\\frac{1200}{100} = 12$"}. Při zvětšení libovolné z hodnot by se jiná musela zmenšit,
                aby byl průměr {"$12$"} zachován; přitom hodnoty {"$0$"} zmenšit nemůžeme (hodnoty se týkají nezáporné veličiny),
                takže by se musela zmenšit jedna z hodnot {"$40$"} - tímpádem {"$\\geq 40$"} by už bylo jen {"$29$"} hodnot. Atd.
            </i>
            </li>
            <li>Rozsah souboru je {"$n = \\sum c_j = 12 + 8 + 4 + 2 =26$"}.
            Relativní četnosti jsou {"$f_j = c_j / n$"}, tedy {"$\\frac{12}{26}, \\frac{8}{26}, \\frac{4}{26}, \\frac{2}{26}$"}.
            Středy {"$x^*_j$"} tříd jsou {"$5, 15, 25, 35$"}.
            Průměr je {"$\\bar{x} = \\sum_{j=1}^{4} f_j x^*_j = \\frac{12}{26} \\cdot 5 + \\frac{8}{26} \\cdot 15 + \\frac{4}{26} \\cdot 25 + \\frac{2}{26} \\cdot 35 \\approx 13.46$"}.
            Může vyjít jinak, než u původního souboru, protože se ztratily přesné původní hodnoty - místo nich jako zástupce používáme středy tříd.
            Pokud by původní data byla {"$12 \\times 0; \\ 8 \\times 10; \\ 4 \\times 20; \\ 2 \\times 30$"}, pak by skutečný průměr byl {"$8.46$"}.
            Pokud by původní data byla {"$12 \\times 9.9; \\ 8 \\times 19.9; \\ 4 \\times 29.9; \\ 2 \\times 39.9$"}, pak by skutečný průměr byl {"$18.36$"}.
            </li>
            <li>
                {"$s_n(y) = |3.1| \\cdot 28.3495 \\approx 87.88$"} g.
            </li>
            <li>
                {"$s_n(y) = s_n(x) = 2.5$"}. Transformace je čistá translace - víme, že nemění rozptyl / směrodatnou odchylku.
            </li>
            <li>Jen v případě, že všechny hodnoty v datovém souboru jsou stejné.
            </li>
        </ol>
        </p>
        <Footer/>
        </ChapterWrapper>
    );
}
